import {parseUnifiedDiff} from '@github-ui/agent-sessions/utils/parse-unified-diff'
import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
import {CopilotCodeReviewFeedback} from '@github-ui/copilot-code-review-feedback/CopilotCodeReviewFeedback'
import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {CheckIcon, ChevronDownIcon, ChevronRightIcon, DashIcon, TriangleDownIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, Link} from '@primer/react'
import {clsx} from 'clsx'
import {type FC, type JSX, type MouseEvent, useMemo, useState} from 'react'
import {
  getDisplayFilePath,
  getFindingFileLocation,
  getFindingFullFileLocation,
  getFindingSeverity,
  getFindingSourceLinks,
  getFindingTitle,
} from './entity-helpers'
import styles from './Finding.module.css'
import {FixFindingButton, FixTaskExpandedStatus, FixTaskHeaderStatus} from './FixFindingStatus'
import type {FindingResolvedReason, Finding as FindingType} from './types'
import {useFixTask} from './use-fix-task'

const RESOLVE_REASONS: Array<{
  reason: FindingResolvedReason
  label: string
  description: string
  icon: JSX.Element
}> = [
  {
    reason: 'addressed',
    label: 'Addressed',
    icon: <CheckIcon />,
    description: 'This finding has been addressed',
  },
  {
    reason: 'wont_fix',
    label: "Won't fix",
    icon: <XIcon />,
    description: "This finding won't be addressed",
  },
  {
    reason: 'incorrect',
    label: 'Incorrect',
    icon: <DashIcon />,
    description: 'This finding is incorrect or unhelpful',
  },
]

interface FindingProps {
  finding: FindingType
  resolved: boolean
  onDismiss: (resolvedReason: FindingResolvedReason) => void
  onReopen: () => void
  onFix: () => void
  feedbackPath: string
  taskBasePath: string
  ccaTaskId?: string
}

export const Finding: FC<FindingProps> = ({
  finding,
  resolved,
  onDismiss,
  onReopen,
  onFix,
  feedbackPath,
  ccaTaskId,
  taskBasePath,
}) => {
  const {taskData, fixTask, taskUrl} = useFixTask({ccaTaskId, taskBasePath})

  const [showDetails, setShowDetails] = useState(false)
  const severity = getFindingSeverity(finding)
  const sourceLinks = getFindingSourceLinks(finding)
  const fileLocation = getFindingFileLocation(finding)
  const fullFileLocation = getFindingFullFileLocation(finding)
  const title = getFindingTitle(finding)
  const shortenedFilePath = getDisplayFilePath(finding.filePath)
  const fileLocationFullPathText =
    fullFileLocation && fullFileLocation !== fileLocation ? (
      <span className="sr-only">{` Full path: ${fullFileLocation}`}</span>
    ) : null
  const shortenedFilePathFullPathText =
    finding.filePath && shortenedFilePath !== finding.filePath ? (
      <span className="sr-only">{` Full path: ${finding.filePath}`}</span>
    ) : null

  const diffLines = useMemo(
    () => (finding.suggestion ? parseUnifiedDiff(finding.suggestion) : []),
    [finding.suggestion],
  )
  const resolvedReasonLabel = finding.resolvedReason
    ? RESOLVE_REASONS.find(({reason}) => reason === finding.resolvedReason)?.label
    : undefined
  const resolvedLabel = resolvedReasonLabel ? `Resolved as ${resolvedReasonLabel}` : undefined

  const handleHeaderClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.closest('a')) return
    setShowDetails(prev => !prev)
  }

  return (
    <li className={styles.Finding}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- keyboard a11y is handled by the nested toggle button */}
      <div className={styles.header} onClick={handleHeaderClick}>
        <button
          type="button"
          className={styles.toggleButton}
          aria-expanded={showDetails}
          aria-label={`Details for: ${title}`}
        >
          {showDetails ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </button>
        <div className={styles.headerContent}>
          <span className="text-semibold color-fg-default">{title}</span>
          <span className="d-flex flex-items-center flex-wrap gap-1 color-fg-muted text-small">
            {severity && (
              <span className={styles.metaItem}>
                <RuleSeverityBadge severity={severity} />
              </span>
            )}
            {sourceLinks.map(link => (
              <span key={`${link.label}-${link.url}`} className={styles.metaItem}>
                <Link href={link.url} className="Link--inTextBlock">
                  {link.label}
                </Link>
              </span>
            ))}
            {fileLocation && (
              <span className={styles.metaItem}>
                {finding.fileUrl ? (
                  <Link href={finding.fileUrl} className="text-mono Link--inTextBlock">
                    <span>{fileLocation}</span>
                    {fileLocationFullPathText}
                  </Link>
                ) : (
                  <span className="text-mono">
                    <span>{fileLocation}</span>
                    {fileLocationFullPathText}
                  </span>
                )}
              </span>
            )}
            {fixTask && (
              <span className={styles.metaItem}>
                <FixTaskHeaderStatus fixTask={fixTask} taskUrl={taskUrl} />
              </span>
            )}
          </span>
        </div>
      </div>
      {showDetails && (
        <div className={styles.expandedContent}>
          <div className={styles.section}>
            <MarkdownRenderer markdown={finding.messageMarkdown ?? finding.message} wrapCodeLines />
          </div>
          {diffLines.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Suggestions</h3>
              <div className={styles.diffPreview}>
                <div className={clsx('d-flex flex-column overflow-hidden', styles.diffEntryHeader)}>
                  <div className="d-flex flex-items-center tmp-px-3 py-2 text-small gap-2 text-mono">
                    <span>
                      <span>{shortenedFilePath}</span>
                      {shortenedFilePathFullPathText}
                    </span>
                  </div>
                  <StaticUnifiedDiffPreview
                    tabSize={4}
                    className={clsx(
                      'm-0 border-bottom borderColor-default overflow-hidden',
                      styles.automatedReviewDiff,
                    )}
                    diffTableClassName="borderColor-default"
                    diffLines={diffLines}
                  />
                </div>
              </div>
            </div>
          )}
          {!diffLines.length && finding.suggestion && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Suggestion</h3>
              <pre className="color-bg-subtle tmp-p-3 rounded-2 overflow-auto text-small">{finding.suggestion}</pre>
            </div>
          )}
          {fixTask && <FixTaskExpandedStatus fixTask={fixTask} taskData={taskData} taskUrl={taskUrl} />}
          <div className="d-flex flex-items-center flex-justify-between tmp-mt-3">
            <CopilotCodeReviewFeedback
              commentId={finding.id}
              feedbackPath={feedbackPath}
              showNegativeFeedbackModal={false}
              additionalParameters={{finding_id: finding.id, feedback_target: 'FINDING'}}
            />
            <div className="d-flex flex-items-center gap-2">
              <FixFindingButton resolved={resolved} fixTask={fixTask} onFix={onFix} />
              {resolved ? (
                <>
                  {resolvedLabel && <span className="text-small color-fg-muted">{resolvedLabel}</span>}
                  <Button onClick={onReopen}>Reopen</Button>
                </>
              ) : (
                <ButtonGroup>
                  <Button onClick={() => onDismiss('addressed')}>Resolve</Button>
                  <ActionMenu>
                    <ActionMenu.Button aria-label="Resolve options" icon={TriangleDownIcon} />
                    <ActionMenu.Overlay width="medium" align="end">
                      <ActionList>
                        {RESOLVE_REASONS.map(({reason, label, description, icon}) => (
                          <ActionList.Item key={reason} onSelect={() => onDismiss(reason)}>
                            <ActionList.LeadingVisual>{icon}</ActionList.LeadingVisual>
                            {label}
                            <ActionList.Description variant="block">{description}</ActionList.Description>
                          </ActionList.Item>
                        ))}
                      </ActionList>
                    </ActionMenu.Overlay>
                  </ActionMenu>
                </ButtonGroup>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  )
}
